import {
  VStack,
  Icon,
  Center,
  HStack,
  Spacer,
  Button,
  Spinner,
  Box,
  Heading,
  Text,
  Img,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { SignupClient } from "../../../../Api/client/CompetitionClient";
import { SignupResponseDto } from "../../../../Api/dto/SignupResponseDtos";
import { SvgComponent as SwishLogo } from "../swishicon";
import {
  paymentStatusFail,
  paymentStatusSuccess,
  paymentStatusWaiting,
} from "../Step5StartPayment/Step5StartPayment";
import { ofiEmail } from "../../../foreningen";

const MAX_SWISH_WAITING_TIME_IN_MS = (30 + 3 * 60) * 1000; // 30 seconds longer than actual swish timeout

export function SwishPaymentBox(props: {
  competitionTag: string;
  email: string;
  signupResponse: SignupResponseDto | undefined;
  onComplete: () => void;
  onRestart: () => void;
}) {
  const [paymentStatus, setPaymentStatus] = useState(paymentStatusWaiting);
  const [startedWaitingTimeInMs, setStartedWaitingTimeInMs] = useState(
    Date.now()
  );
  const token: string | null =
    props.signupResponse?.paymentRequestToken ?? null;

  useEffect(() => {
    if (
      props.signupResponse != null &&
      !props.signupResponse.waitingForPayment
    ) {
      setPaymentStatus(paymentStatusSuccess);
    }
  }, [props.signupResponse, props.signupResponse?.waitingForPayment]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (Date.now() - startedWaitingTimeInMs > MAX_SWISH_WAITING_TIME_IN_MS) {
        setPaymentStatus(paymentStatusFail);
      } else if (paymentStatus === paymentStatusWaiting) {
        SignupClient.checkPaymentStatus(
          props.signupResponse?.paymentStatusTag ?? ""
        )
          .then((result) => {
            if (result.body?.paymentStatusTag)
              setPaymentStatus(result.body?.status ?? "");
          })
          .catch(() => {});
      }
    }, 5000); // This will execute the code every 5 seconds
    // Cleanup the interval when the component is unmounted or when dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [props.signupResponse?.paymentStatusTag, startedWaitingTimeInMs]);

  return (
    <VStack
      background={
        paymentStatus == paymentStatusSuccess
          ? "green.200"
          : paymentStatus == paymentStatusFail
          ? "red.400"
          : "blue.50"
      }
    >
      <Box
        key={"paymentbox"}
        border="1px"
        borderColor="gray.300"
        p={4}
        width={"sm"}
        rounded="xl"
      >
        <Heading size="sm" mb={2}>
          Betalning
        </Heading>
        <Box
          key={"swishBox"}
          border="1px"
          borderColor="gray.300"
          padding={4}
          rounded="xl"
          margin={"5px"}
        >
          <Icon
            as={SwishLogo}
            boxSize={"xxs"}
            height={"40px"}
            width={"100%"}
            marginBottom={"10px"}
          />
          <Text
            marginBottom={"10px"}
            fontStyle={"italic"}
            textColor={"gray.500"}
          >
            För att slutföra din anmälan måste du betala din anmälningsavgift
            med Swish. När anmälningen lyckats så skickas ett bekräftelsemail
            till {props.email}.
          </Text>

          <Center>
            <Text marginBottom={"20px"} fontSize={"lg"} width={"400px"}>
              Betala anmälningsavgiften genom att scanna QR-koden med din
              Swish-app:
            </Text>
          </Center>
          <Center>{token && <QRDisplayComponent swishToken={token} />}</Center>
          <Center>
            <Text
              marginTop={"20px"}
              marginBottom={"20px"}
              fontSize={"lg"}
              width={"400px"}
              textAlign={"center"}
            >
              Eller öppna Swish appen på den här enheten:
            </Text>
          </Center>
          {token && (
            <Center>
              <OpenSwishButton token={token} />
            </Center>
          )}
          <div
            style={{ width: "100%", height: "1px", background: "black" }}
          ></div>
          <div
            style={{
              marginTop: "2px",
              width: "100%",
              height: "1px",
              background: "black",
            }}
          ></div>
          <VStack>
            <HStack width={"100%"} marginTop={"10px"}>
              <Spacer />
              <VStack hidden={paymentStatus != paymentStatusWaiting}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
                <Text>
                  Väntar på att du slutför betalningen i Swish-appen...
                </Text>
              </VStack>
            </HStack>
            {paymentStatus != paymentStatusWaiting && (
              <Text fontWeight={"bold"}>Betalningstatus: {paymentStatus}</Text>
            )}
            {paymentStatus == paymentStatusFail && (
              <Text fontWeight={"bold"} wordBreak={"break-all"}>
                Betalningen misslyckats. Det kan bero på att du avbrutit
                transaktionen, ditt saldo är för lågt eller något liknande. Du
                är välkommen att försöka igen, eller kontakta oss på {ofiEmail}{" "}
                om problemen kvarstår!
              </Text>
            )}
            {paymentStatus == paymentStatusFail && (
              <Button colorScheme="yellow" onClick={props.onRestart}>
                Försök igen
              </Button>
            )}
            {paymentStatus == paymentStatusSuccess && (
              <Button colorScheme="green" onClick={props.onComplete}>
                Återgå till startsidan
              </Button>
            )}
          </VStack>
        </Box>
      </Box>
    </VStack>
  );
}

const QRDisplayComponent = (props: { swishToken: string | null }) => {
  const [qrImage, setQrImage] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (props.swishToken != null) {
        SignupClient.fetchQRCodeFromBackend(props.swishToken).then(
          (response) => {
            setQrImage(`data:image/png;base64,${response}`);
          }
        );
      }
    } catch (error) {
      console.error("Error fetching QR code:", error);
    }
  }, [props.swishToken]);

  return (
    <div>{qrImage && <Img src={qrImage} alt={qrImage ?? "IMAGE"} />} </div>
  );
};

const OpenSwishButton = (props: { token: string; callback?: string }) => {
  const deepLinkUrl =
    props.callback != null
      ? `swish://paymentrequest?token=${props.token}&callbackurl=${props.callback}`
      : `swish://paymentrequest?token=${props.token}`;

  return (
    <VStack>
      <Button
        colorScheme="pink"
        marginBottom={"40px"}
        onClick={() => {
          window.location.href = deepLinkUrl;
        }}
      >
        Öppna Swish på den här enheten
      </Button>
    </VStack>
  );
};
